import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchDutyRate, DutyRateResponse, Country, HsLookupResponse } from "../services/api";
import { PageTitle } from "./PageTitle";
import { ResultsSkeleton } from "./ResultsSkeleton";
import { VatRate } from "./VatRate";

// --- Location State ---
interface LocationState {
  product: HsLookupResponse & { title?: string; customsDuty?: number };
  query: {
    term: string;
    picked?: string;
    country: Country | string;
    countryId?: number;
  };
}

export const ResultDetail: FC = () => {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const navigate = useNavigate();

  const [dutyRate, setDutyRate] = useState<DutyRateResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  //control VAT open/close here
  const [showVat, setShowVat] = useState<boolean>(false);
  const [copiedHsCode, setCopiedHsCode] = useState<boolean>(false);

  useEffect(() => {
    if (!state) {
      navigate("/");
      return;
    }

    const { product, query } = state;
    const hsCode = product?.hsCode || "";
    const countryId = (query?.country as Country)?.id || query?.countryId;

    if (hsCode && countryId) {
      fetchDutyRate(hsCode, countryId)
        .then((res) => setDutyRate(res))
        .catch((err) => console.error("Error fetching duty rates:", err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [state, navigate]);

  if (!state) {
    return <div>Redirecting…</div>;
  }

  const { product, query } = state;
  const hsCode = product?.hsCodeFormatted || product?.hsCode || "";
  const title =
    product?.title ||
    product?.description ||
    query?.picked ||
    query?.term ||
    "";

  //raw code to send backend
  const hsCodeToSend = product?.hsCode || "";

  const isNumericTerm = /^\d+$/.test(query?.term);

  if (loading) return <ResultsSkeleton />;

  const total = dutyRate?.customsDuty;

  const handleCopyHsCode = async () => {
    const value = dutyRate?.hsCodeFormatted || hsCode;
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedHsCode(true);
      setTimeout(() => setCopiedHsCode(false), 1500);
    } catch (err) {
      console.error("Failed to copy HS code", err);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="min-h-screen detail-bg">
      <div className="max-w-[1200px] w-[92vw] mx-auto py-12">
        <PageTitle />

        {/* Query info */}
        <div className="flex items-center gap-2 mb-5">
          <span className="inline-flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 rounded-full shadow">
            {query?.term}
            <button
              aria-label="remove term"
              onClick={() =>
                window.history.length > 2 ? navigate(-2) : navigate("/")
              }
            >
              ×
            </button>
          </span>
          <span className="text-neutral-500">›</span>
          <span className="inline-flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 rounded-full shadow">
            {query?.picked || hsCode}
            <button
              aria-label="remove picked"
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate("/")
              }
            >
              ×
            </button>
          </span>
          <span className="text-neutral-600">
            from {typeof query?.country === "string" ? query.country : query?.country?.name}
          </span>
        </div>

        {/* Product Detail */}
        <div className="grid">
          <section className="bg-white rounded-2xl border border-neutral-200 shadow-xl p-6">
            <h3 className="text-neutral-500 font-semibold mb-2">Product</h3>
            <p className="text-lg leading-relaxed mb-4">{title}</p>

            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-sm text-neutral-500">Duty rate</div>
                <div className="font-bold">{dutyRate?.customsDuty ? `${total}%` : "N/A"}</div>
              </div>
              <div>
                <div className="text-sm text-neutral-500">HS Code</div>
                <div className="font-bold flex items-center gap-2">
                  <span>{dutyRate?.hsCodeFormatted || hsCode}</span>
                  <button
                    type="button"
                    aria-label="Copy HS code"
                    title={copiedHsCode ? "Copied!" : "Copy HS code"}
                    onClick={handleCopyHsCode}
                    className="text-neutral-400 hover:text-neutral-700 transition"
                  >
                    {copiedHsCode ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <div className="text-sm text-neutral-500">Origin Code</div>
                <div className="font-bold">{dutyRate?.originCode || "N/A"}</div>
              </div>
            </div>

            {/* Explanation */}
            <div className="text-neutral-500 font-semibold mt-3">How was this determined:</div>

            {isNumericTerm ? (
              dutyRate?.id === 0 ? (
                <div className="text-neutral-500 font-semibold">
                  The customs code “{query?.term}” does not have a special tariff for the
                  country of origin{" "}
                  {typeof query?.country === "string" ? query.country : query?.country?.name}
                  , therefore standard duties rate {dutyRate?.customsDuty}% is applicable.
                </div>
              ) : (
                <div className="text-neutral-500 font-semibold">
                  The customs code “{query?.term}” falls under the legal basis{" "}
                  {dutyRate?.measureType} - {dutyRate?.legalBase} effective since{" "}
                  {formatDate(dutyRate?.startDate as unknown as string)}. Since the
                  country of origin is{" "}
                  {typeof query?.country === "string" ? query.country : query?.country?.name}
                  , customs duties are: {dutyRate?.customsDuty}%
                </div>
              )
            ) : (
              <div className="text-neutral-500 font-semibold">
                The product “{query?.term}” was classified under HS code{" "}
                {dutyRate?.hsCodeFormatted}. Legal basis for the duties is{" "}
                {dutyRate?.measureType} - {dutyRate?.legalBase} effective since{" "}
                {formatDate(dutyRate?.startDate as unknown as string)}. Since the
                country of origin is{" "}
                {typeof query?.country === "string" ? query.country : query?.country?.name}
                , customs duties are: {dutyRate?.customsDuty}%
              </div>
            )}
          </section>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <button
            className="text-blue-700"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          >
            ← Back
          </button>

          <button
            type="button"
            className="bg-white border border-neutral-300 rounded-xl px-6 py-3 shadow hover:shadow-md font-semibold"
            onClick={() => setShowVat((s) => !s)}
            disabled={!hsCodeToSend}
            title={!hsCodeToSend ? "HS code is missing" : undefined}
          >
            Get VAT rate
          </button>
        </div>

        <VatRate hsCode={hsCodeToSend} isOpen={showVat} />
      </div>
    </div>
  );
};
